import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Download, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * GuestHubQRDialog
 * ----------------
 * Renders a printable QR code that points to the public GuestHub portal
 * for a given property. The QR is generated client-side as a PNG data-URL
 * (no external API call), then offered to the user as Download / Print.
 *
 * Print template is intentionally minimal — large QR, property name, and
 * a single instruction line — so a hotel can fold it into in-room cards.
 */
export const GuestHubQRDialog = ({ open, onOpenChange, propertyName, slug, url }) => {
  const [dataUrl, setDataUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const printIframeRef = useRef(null);

  useEffect(() => {
    if (!open || !url) return;
    let cancelled = false;
    setGenerating(true);
    QRCode.toDataURL(url, {
      width: 600,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#26302A', light: '#FFFFFF' },
    })
      .then((d) => { if (!cancelled) setDataUrl(d); })
      .catch(() => { if (!cancelled) toast.error('Could not generate QR code'); })
      .finally(() => { if (!cancelled) setGenerating(false); });
    return () => { cancelled = true; };
  }, [open, url]);

  const handleDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    const safeName = (propertyName || slug || 'guesthub').replace(/[^a-z0-9-]/gi, '-').toLowerCase();
    a.download = `guesthub-${safeName}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handlePrint = () => {
    if (!dataUrl) return;
    const w = window.open('', '_blank', 'noopener,noreferrer,width=800,height=900');
    if (!w) {
      toast.error('Please allow pop-ups to print the QR card');
      return;
    }
    w.document.write(`<!doctype html>
<html><head><title>${propertyName || 'Guest Hub'} — QR card</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; margin: 0; padding: 40px; color: #1c1917; background: #FAF8F4; }
  .card { max-width: 420px; margin: 0 auto; background: #fff; border: 1px solid #e7e5e4; border-radius: 16px; padding: 28px; text-align: center; }
  .eyebrow { font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; color: #3F617E; margin-bottom: 10px; }
  h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 500; font-size: 26px; margin: 0 0 6px; color: #1c1917; }
  .sub { font-size: 13px; color: #57534e; margin: 0 0 22px; }
  img { width: 260px; height: 260px; border: 1px solid #f5f5f4; border-radius: 12px; padding: 8px; background: #fff; }
  .instr { font-size: 13.5px; color: #44403c; margin-top: 22px; line-height: 1.55; }
  .url { font-size: 11px; color: #78716c; word-break: break-all; margin-top: 16px; }
  .foot { font-size: 10.5px; color: #a8a29e; margin-top: 22px; letter-spacing: 0.18em; text-transform: uppercase; }
  @media print { body { background: #fff; padding: 0; } .card { border: none; } }
</style></head>
<body>
  <div class="card">
    <div class="eyebrow">Guest Hub</div>
    <h1>${propertyName || 'Welcome'}</h1>
    <p class="sub">Scan to request anything — housekeeping, maintenance, transport, late checkout.</p>
    <img src="${dataUrl}" alt="Guest Hub QR code" />
    <p class="instr">Point your phone camera at the code, then tap the notification to open the request form.</p>
    <p class="url">${url}</p>
    <p class="foot">Maris Desk</p>
  </div>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
</body></html>`);
    w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="guesthub-qr-dialog">
        <DialogHeader>
          <DialogTitle data-testid="guesthub-qr-title">GuestHub QR — {propertyName}</DialogTitle>
          <DialogDescription>
            Share this QR code with guests so they can submit requests directly to GuestHub.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center pt-2">
          <div className="rounded-xl border border-stone-200 bg-white p-3 shadow-sm">
            {generating || !dataUrl ? (
              <div className="h-[240px] w-[240px] grid place-items-center text-stone-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <img
                src={dataUrl}
                alt="Guest Hub QR code"
                className="h-[240px] w-[240px]"
                data-testid="guesthub-qr-image"
              />
            )}
          </div>
          <p className="text-[11px] text-stone-500 mt-3 text-center break-all max-w-[280px]" data-testid="guesthub-qr-url">
            {url}
          </p>
        </div>

        <div className="flex gap-2 pt-4">
          <Button
            variant="outline"
            className="flex-1 rounded-full"
            onClick={handleDownload}
            disabled={!dataUrl}
            data-testid="guesthub-qr-download"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Download PNG
          </Button>
          <Button
            className="flex-1 bg-[#3F617E] hover:bg-[#345470] rounded-full"
            onClick={handlePrint}
            disabled={!dataUrl}
            data-testid="guesthub-qr-print"
          >
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print card
          </Button>
        </div>

        <iframe ref={printIframeRef} title="qr-print-frame" style={{ display: 'none' }} />
      </DialogContent>
    </Dialog>
  );
};

export default GuestHubQRDialog;
